import { roundNumber, roundNumberToSignificant } from './number';
import { FAIR_VALUE_PRECISION } from './schema';

type FairValueArgs = {
  discountRatePercentage: number;
  futurePriceEarningsMultiple: number;
  futureProfitMarginPercentage: number;
  futureRevenuePercentage: number;
  futureSharesPercentage: number;
  presentRevenue: number;
  presentShares: number;
  timePeriod: number;
};
/**
 * Calculate an estimate fair value based on company analysis data
 * @param {number} discountRatePercentage Discount rate adjusts future values to today's terms (e.g 10%)
 * @param {number} futurePriceEarningsMultiple PE ratio (e.g 34.25x)
 * @param {number} futureProfitMarginPercentage Profit margin (e.g 25.5%)
 * @param {number} futureRevenuePercentage Change in revenue per year (e.g 5.5%)
 * @param {number} futureSharesPercentage Change in shares outstanding per year (e.g 5.5%)
 * @param {number} presentRevenue Current revenue of the company (e.g 383,285,000,000)
 * @param {number} presentShares Current shares outstanding of the company (e.g 15,552,752,000)
 * @param {number} timePeriod Forecast period of the calculation (e.g 3, 5 or 10 years)
 */
export const calculateFairValue = ({
  discountRatePercentage,
  futurePriceEarningsMultiple,
  futureProfitMarginPercentage,
  futureRevenuePercentage,
  futureSharesPercentage,
  presentRevenue,
  presentShares,
  timePeriod,
}: FairValueArgs) => {
  const revenueRate = 1 + futureRevenuePercentage / 100;
  const futureRevenue = presentRevenue * revenueRate ** timePeriod;
  const futureProfitMargin = futureProfitMarginPercentage / 100;
  const futureEarnings = futureProfitMargin * futureRevenue;
  const futureMarketCapitalisation =
    futureEarnings * futurePriceEarningsMultiple;
  const futureSharesRate = 1 + futureSharesPercentage;
  const futureShares = presentShares * futureSharesRate ** timePeriod;
  const futureSharePrice = futureMarketCapitalisation / futureShares;
  const discountRate = 1 + discountRatePercentage / 100;
  const estimatedFairValue = futureSharePrice / discountRate ** timePeriod;

  return roundNumberToSignificant(estimatedFairValue, FAIR_VALUE_PRECISION);
};

type CalculatePriceEarningsMultipleArgs = {
  discountRatePercentage: number;
  presentSharePrice: number;
  futureProfitMarginPercentage: number;
  futureRevenuePercentage: number;
  futureSharesPercentage: number;
  presentRevenue: number;
  presentShares: number;
  timePeriod: number;
};
/**
 * Calculate an estimate fair value based on company analysis data
 * @param {number} discountRatePercentage Discount rate adjusts future values to today's terms (e.g 10%)
 * @param {number} presentSharePrice Fair value or present share price
 * @param {number} futureProfitMarginPercentage Profit margin (e.g 25.5%)
 * @param {number} futureRevenuePercentage Change in revenue per year (e.g 5.5%)
 * @param {number} futureSharesPercentage Change in shares outstanding per year (e.g 5.5%)
 * @param {number} presentRevenue Current revenue of the company (e.g 383,285,000,000)
 * @param {number} presentShares Current shares outstanding of the company (e.g 15,552,752,000)
 * @param {number} timePeriod Forecast period of the calculation (e.g 3, 5 or 10 years)
 */
export const calculatePriceEarningsMultiple = ({
  discountRatePercentage,
  presentSharePrice,
  futureProfitMarginPercentage,
  futureRevenuePercentage,
  futureSharesPercentage,
  presentRevenue,
  presentShares,
  timePeriod,
}: CalculatePriceEarningsMultipleArgs) => {
  const revenueRate = 1 + futureRevenuePercentage / 100;
  const futureRevenue = presentRevenue * revenueRate ** timePeriod;
  const futureProfitMargin = futureProfitMarginPercentage / 100;
  const futureEarnings = futureProfitMargin * futureRevenue;
  const futureSharesRate = 1 + futureSharesPercentage;
  const futureShares = presentShares * futureSharesRate ** timePeriod;
  const discountRate = 1 + discountRatePercentage / 100;
  const futureSharePrice = presentSharePrice * discountRate ** timePeriod;
  return futureSharePrice / (futureEarnings / futureShares);
};

type RevenueGrowthArgs = {
  discountRatePercentage: number;
  fairValue: number;
  futurePriceEarningsMultiple: number;
  futureProfitMarginPercentage: number;
  futureSharesPercentage: number;
  presentRevenue: number;
  presentShares: number;
  timePeriod: number;
};
/**
 * Calculate an estimate revenue growth based on company analysis data
 * @param {number} discountRatePercentage Discount rate adjusts future values to today's terms (e.g 10%)
 * @param {number} fairValue Estimated fair value of the company (e.g 876.85)
 * @param {number} futurePriceEarningsMultiple PE ratio (e.g 34.25x)
 * @param {number} futureProfitMarginPercentage Profit margin (e.g 25.5%)
 * @param {number} futureSharesPercentage Change in shares outstanding per year (e.g 5.5%)
 * @param {number} presentRevenue Current revenue of the company (e.g 383,285,000,000)
 * @param {number} presentShares Current shares outstanding of the company (e.g 15,552,752,000)
 * @param {number} timePeriod Forecast period of the calculation (e.g 3, 5 or 10 years)
 */
export const calculateRevenueGrowth = ({
  discountRatePercentage,
  fairValue,
  futurePriceEarningsMultiple,
  futureProfitMarginPercentage,
  futureSharesPercentage,
  presentRevenue,
  presentShares,
  timePeriod,
}: RevenueGrowthArgs) => {
  const futureSharesRate = 1 + futureSharesPercentage;
  const futureSharesOutstanding =
    presentShares * futureSharesRate ** timePeriod;
  const discountRate = 1 + discountRatePercentage / 100;
  const numerator =
    fairValue * discountRate ** timePeriod * futureSharesOutstanding;
  const denominator =
    (futureProfitMarginPercentage / 100) * futurePriceEarningsMultiple;

  // Prevent division by 0
  if (!presentRevenue || !denominator) return 0;

  const radicand = numerator / denominator / presentRevenue;
  const estimatedRevenueGrowth = (radicand ** (1 / timePeriod) - 1) * 100;

  return roundNumber(estimatedRevenueGrowth, 2);
};
